import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import {
  type AuthClaims,
  type AdminRole,
  getCurrentSession,
  logout as cognitoLogout,
} from './cognito';

type AuthState =
  | { kind: 'loading' }
  | { kind: 'signed-out' }
  | { kind: 'signed-in'; claims: AuthClaims };

type AuthContextValue = AuthState & {
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
  hasRole: (minRole: AdminRole) => boolean;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const ROLE_RANK: Record<AdminRole, number> = {
  'read-only': 1,
  csr: 2,
  'support-lead': 3,
  admin: 4,
  superadmin: 5,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ kind: 'loading' });

  const refresh = async () => {
    const s = await getCurrentSession();
    setState(s ? { kind: 'signed-in', claims: s.claims } : { kind: 'signed-out' });
  };

  const signOut = async () => {
    await cognitoLogout();
    setState({ kind: 'signed-out' });
  };

  const hasRole = (minRole: AdminRole) => {
    if (state.kind !== 'signed-in') return false;
    return ROLE_RANK[state.claims.role] >= ROLE_RANK[minRole];
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, refresh, signOut, hasRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
